<template>
  <div class="radar-player-3d">
    <div class="panel" :style="panelStyle">
      <button
        class="collapse-btn"
        :style="collapseBtnStyle"
        @click="panelCollapsed = !panelCollapsed"
      >
        <StepForwardOutlined v-if="panelCollapsed" />
        <StepBackwardOutlined v-else />
      </button>

      <template v-if="!panelCollapsed">
        <button
          class="default-btn"
          :style="defaultBtnStyle"
          @click="resetSignal++"
          title="重置默认视角"
        >
          🔄 默认视角
        </button>

        <button class="default-btn" :style="defaultBtnStyle" @click="frontSignal++" title="正视">
          ➡️ 正视视角
        </button>

        <button class="default-btn" :style="defaultBtnStyle" @click="topSignal++" title="俯视">
          ⬇️ 俯视视角
        </button>

        <button
          class="auto-btn"
          :style="autoBtnStyle(autoActive)"
          @click="toggleAutoSignal++"
          title="自动环绕"
        >
          {{ autoActive ? '⏹️ 停止环绕' : '▶️ 开始环绕' }}
        </button>
      </template>
    </div>

    <div ref="canvasContainer" class="canvas-container">
      <RadarPoints
        ref="radarPointsRef"
        :points="renderTargets"
        :size="pointSize"
        :color="pointColor"
        :emissive="'#0ff'"
      />
      <RadarPointCloud
        ref="radarPointCloudRef"
        :points="renderPointClouds"
        :size="pointSize / 3"
        :opacity="0.6"
      />
      <RadarDeviceMarker ref="radarDeviceMarkerRef" :dev-param="devParam" />
      <RadarScanBeam ref="radarScanBeamRef" :dev-param="devParam" />
      <RadarPlayer3D_ThickAxes ref="thickAxesRef" :length="5" :radius="0.002" />
      <RadarPlayer3D_TrackingRegion
        v-if="trackingRegion"
        ref="trackingRegionRef"
        :region="trackingRegion"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, watch, shallowRef, type CSSProperties } from 'vue'
import * as THREE from 'three'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js'
import { StepForwardOutlined, StepBackwardOutlined } from '@ant-design/icons-vue'

// 导入子组件
import RadarPoints from './RadarPlayer3D_Points.vue'
import RadarPointCloud from './RadarPlayer3D_PointCloud.vue'
import RadarDeviceMarker from './RadarPlayer3D_DeviceMarker.vue'
import RadarScanBeam from './RadarPlayer3D_ScanBeam.vue'
import RadarPlayer3D_ThickAxes from './RadarPlayer3D_ThickAxes.vue'
import RadarPlayer3D_TrackingRegion from './RadarPlayer3D_TrackingRegion.vue'

defineOptions({ name: 'BasePlayer3D' })

// 导入类型定义
import type {
  RadarPlayer3DProps,
  RadarTarget,
  PointCloud,
  RadarPointsExpose,
  RadarPointCloudExpose,
} from './types'

// Props
const props = withDefaults(defineProps<RadarPlayer3DProps>(), {
  devParam: null,
  radarTargets: () => [],
  pointClouds: () => [],
  trackingRegion: null,
  showGrid: true,
  showAxis: true,
  pointSize: 0.06,
  pointColor: '#00ffff',
  staleTimeoutMs: 3000,
  isReplay: false,
})

// 状态
const panelCollapsed = ref(false)
const resetSignal = ref(0)
const frontSignal = ref(0)
const topSignal = ref(0)
const toggleAutoSignal = ref(0)
const autoActive = ref(false)
const renderTargets = ref<RadarTarget[]>([])
const renderPointClouds = ref<PointCloud[]>([])
const staleTimerRef = ref<number | null>(null)
const canvasContainer = ref<HTMLElement | null>(null)

// 3D场景相关
const scene = shallowRef<THREE.Scene | null>(null)
const camera = shallowRef<THREE.PerspectiveCamera | null>(null)
const renderer = shallowRef<THREE.WebGLRenderer | null>(null)
const controls = shallowRef<OrbitControls | null>(null)

// 子组件引用
const radarPointsRef = ref<(InstanceType<typeof RadarPoints> & RadarPointsExpose) | null>(null)
const radarPointCloudRef = ref<
  (InstanceType<typeof RadarPointCloud> & RadarPointCloudExpose) | null
>(null)
const radarDeviceMarkerRef = ref<InstanceType<typeof RadarDeviceMarker> | null>(null)
const radarScanBeamRef = ref<InstanceType<typeof RadarScanBeam> | null>(null)
const thickAxesRef = ref<InstanceType<typeof RadarPlayer3D_ThickAxes> | null>(null)
const trackingRegionRef = ref<InstanceType<typeof RadarPlayer3D_TrackingRegion> | null>(null)

// 样式
const panelStyle: CSSProperties = {
  position: 'absolute',
  bottom: '12px',
  left: '12px',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '8px 12px',
  background: 'rgba(20, 20, 40, 0.6)',
  backdropFilter: 'blur(12px)',
  WebkitBackdropFilter: 'blur(12px)',
  borderRadius: '12px',
  border: '1px solid rgba(100,200,255,0.25)',
  zIndex: 10,
  boxShadow: '0 4px 16px rgba(0,0,0,0.3)',
  userSelect: 'none',
}

const buttonBaseStyle = {
  padding: '6px 14px',
  border: 'none',
  borderRadius: '8px',
  cursor: 'pointer',
  fontSize: '13px',
  fontWeight: 500,
  transition: 'all 0.2s ease',
  boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '4px',
  outline: 'none',
}

const defaultBtnStyle = {
  ...buttonBaseStyle,
  background: 'rgba(255,255,255,0.1)',
  color: '#00ffff',
  border: '1px solid rgba(0,255,255,0.5)',
}

const autoBtnStyle = (active: boolean) => ({
  ...buttonBaseStyle,
  background: active ? 'rgba(0,180,255,0.3)' : 'rgba(255,255,255,0.1)',
  color: active ? '#fff' : '#00ffff',
  border: active ? '1px solid rgba(0,255,255,0.8)' : '1px solid rgba(0,255,255,0.5)',
})

const collapseBtnStyle = {
  ...buttonBaseStyle,
  background: 'rgba(0,50,80,0.4)',
  color: '#00ffff',
  fontSize: '16px',
  fontWeight: 600,
  border: '1px solid rgba(0,255,255,0.5)',
}

// 辅助函数
const clearStaleTimer = () => {
  if (staleTimerRef.value) {
    clearTimeout(staleTimerRef.value)
    staleTimerRef.value = null
  }
}

const clearRenderData = () => {
  renderTargets.value = []
  renderPointClouds.value = []
}

// 平滑过渡动画状态
let cameraTransitionId: number | null = null
const targetPosRef = ref(new THREE.Vector3(3.75, -3.75, 2.0))
const targetLookAtRef = ref(new THREE.Vector3(0, 0, 1))
const isTransitioning = ref(false)

// 平滑移动循环
const smoothMove = () => {
  if (!camera.value || !controls.value || !isTransitioning.value) return

  const currentPos = camera.value.position.clone()
  const currentLook = controls.value.target.clone()

  currentPos.lerp(targetPosRef.value, 0.25)
  currentLook.lerp(targetLookAtRef.value, 0.25)

  camera.value.position.copy(currentPos)
  camera.value.lookAt(currentLook)
  controls.value.target.copy(currentLook)
  controls.value.update()

  if (currentPos.distanceTo(targetPosRef.value) > 0.01) {
    cameraTransitionId = requestAnimationFrame(smoothMove)
  } else {
    isTransitioning.value = false
  }
}

// 重置相机到默认视角 - 平滑过渡
const resetCamera = () => {
  if (!camera.value || !controls.value) return

  // 停止自动环绕
  autoActive.value = false
  stopAutoRotate()
  if (cameraTransitionId) {
    cancelAnimationFrame(cameraTransitionId)
  }

  targetPosRef.value.set(3.75, -3.75, 2.0)
  targetLookAtRef.value.set(0, 0, 1)
  // 恢复相机up方向为z轴正方向，与场景初始化一致
  camera.value.up.set(0, 0, 1)
  isTransitioning.value = true
  smoothMove()
}

// 设置正视视角 - 平滑过渡
const setFrontView = () => {
  if (!camera.value || !controls.value) return

  // 停止自动环绕
  autoActive.value = false
  stopAutoRotate()
  if (cameraTransitionId) {
    cancelAnimationFrame(cameraTransitionId)
  }

  // 设置目标位置和看向点，使相机看向y轴正方向
  targetPosRef.value.set(0, -6, 2)
  targetLookAtRef.value.set(0, 0, 1)
  // 恢复相机up方向为z轴正方向，与场景初始化一致
  camera.value.up.set(0, 0, 1)
  isTransitioning.value = true
  smoothMove()
}

// 设置俯视视角 - 平滑过渡
const setTopView = () => {
  if (!camera.value || !controls.value) return

  // 停止自动环绕
  autoActive.value = false
  stopAutoRotate()
  if (cameraTransitionId) {
    cancelAnimationFrame(cameraTransitionId)
  }

  // 根据设备参数计算合适的高度
  const height = props.devParam?.z_cm ? props.devParam.z_cm / 100 + 5 : 8

  // 设置目标位置和看向点
  targetPosRef.value.set(0, 0, height)
  targetLookAtRef.value.set(0, 0, 0)
  // 保持相机up方向与场景初始化一致，确保鼠标交互正常
  camera.value.up.set(0, 0, 1)
  isTransitioning.value = true
  smoothMove()
}

// 自动环绕动画状态
let autoRotateAnimationId: number | null = null
const autoRotateSpeed = 0.005 // 与React版本一致

// 自动环绕动画 - 允许鼠标调整视角
const startAutoRotate = () => {
  if (!camera.value || !controls.value) return

  // 启用轨道控制器的交互
  controls.value.enabled = true

  const animate = () => {
    if (!autoActive.value || !camera.value || !controls.value) {
      autoRotateAnimationId = null
      return
    }

    // 计算相机到目标点的向量
    const targetPoint = new THREE.Vector3(0, 0, 1)
    const cameraToTarget = new THREE.Vector3()
    cameraToTarget.subVectors(camera.value.position, targetPoint)

    // 绕目标点旋转相机
    cameraToTarget.applyAxisAngle(new THREE.Vector3(0, 0, 1), autoRotateSpeed)

    // 更新相机位置
    camera.value.position.copy(targetPoint).add(cameraToTarget)

    // 看向目标点
    camera.value.lookAt(targetPoint)
    controls.value.target.copy(targetPoint)
    controls.value.update()

    autoRotateAnimationId = requestAnimationFrame(animate)
  }

  if (!autoRotateAnimationId) {
    autoRotateAnimationId = requestAnimationFrame(animate)
  }
}

// 停止自动环绕
const stopAutoRotate = () => {
  if (autoRotateAnimationId) {
    cancelAnimationFrame(autoRotateAnimationId)
    autoRotateAnimationId = null
  }
}

// 添加子组件到场景的函数
const addChildComponentsToScene = () => {
  if (!scene.value) return

  // 清空现有子组件
  const childrenToRemove: THREE.Object3D[] = []
  scene.value.traverse((child) => {
    if (child.userData.isChildComponent) {
      childrenToRemove.push(child)
    }
  })
  childrenToRemove.forEach((child) => scene.value?.remove(child))

  // 添加雷达点
  if (radarPointsRef.value?.group) {
    radarPointsRef.value.group.userData.isChildComponent = true
    scene.value.add(radarPointsRef.value.group)
  }
  // 添加点云
  if (radarPointCloudRef.value?.group) {
    radarPointCloudRef.value.group.userData.isChildComponent = true
    scene.value.add(radarPointCloudRef.value.group)
  }
  // 添加设备标记
  if (radarDeviceMarkerRef.value?.scene) {
    radarDeviceMarkerRef.value.scene.userData.isChildComponent = true
    scene.value.add(radarDeviceMarkerRef.value.scene)
  }
  // 同时添加设备标记的边缘线
  if (radarDeviceMarkerRef.value?.edges) {
    radarDeviceMarkerRef.value.edges.userData.isChildComponent = true
    scene.value.add(radarDeviceMarkerRef.value.edges)
  }
  // 添加扫描光束
  if (radarScanBeamRef.value?.beamsGroup) {
    radarScanBeamRef.value.beamsGroup.userData.isChildComponent = true
    scene.value.add(radarScanBeamRef.value.beamsGroup)
  }
  // 添加坐标轴
  if (thickAxesRef.value?.group) {
    thickAxesRef.value.group.userData.isChildComponent = true
    scene.value.add(thickAxesRef.value.group)
  }
  // 添加跟踪区域
  if (trackingRegionRef.value?.group) {
    trackingRegionRef.value.group.userData.isChildComponent = true
    scene.value.add(trackingRegionRef.value.group)
  }
}

// 初始化渲染数据
renderTargets.value = props.radarTargets || []
renderPointClouds.value = props.pointClouds || []

// 初始化3D场景
const initScene = () => {
  if (!canvasContainer.value) return

  // 创建场景
  scene.value = new THREE.Scene()
  scene.value.background = new THREE.Color(0x000000)

  // 创建相机
  camera.value = new THREE.PerspectiveCamera(
    60,
    canvasContainer.value.clientWidth / canvasContainer.value.clientHeight,
    0.1,
    1000
  )
  camera.value.position.set(3.75, -3.75, 2.0)
  camera.value.up.set(0, 0, 1)

  // 创建渲染器
  renderer.value = new THREE.WebGLRenderer({ antialias: true })
  renderer.value.setSize(canvasContainer.value.clientWidth, canvasContainer.value.clientHeight)
  renderer.value.setPixelRatio(window.devicePixelRatio)
  canvasContainer.value.appendChild(renderer.value.domElement)

  // 添加轨道控制器
  controls.value = new OrbitControls(camera.value, renderer.value.domElement)
  controls.value.enableDamping = true
  controls.value.dampingFactor = 0.1
  controls.value.target.set(0, 0, 1)
  controls.value.minPolarAngle = 0
  controls.value.maxPolarAngle = Math.PI

  // 添加光源
  const ambientLight = new THREE.AmbientLight(0xffffff, 0.3)
  scene.value.add(ambientLight)

  const pointLight1 = new THREE.PointLight(0xffffff, 0.6)
  pointLight1.position.set(10, 10, 10)
  scene.value.add(pointLight1)

  const pointLight2 = new THREE.PointLight(0xffffff, 0.4)
  pointLight2.position.set(-10, -5, 5)
  scene.value.add(pointLight2)

  const directionalLight = new THREE.DirectionalLight(0xffffff, 1)
  directionalLight.position.set(5, 5, 10)
  scene.value.add(directionalLight)

  // 添加网格
  if (props.showGrid) {
    const gridHelper = new THREE.GridHelper(10, 20, 0x444455, 0x222233)
    gridHelper.rotation.x = Math.PI / 2
    scene.value.add(gridHelper)
  }

  // 初始添加
  addChildComponentsToScene()

  // 监听子组件变化，重新添加到场景
  watch(
    () => [
      radarPointsRef.value?.group,
      radarPointCloudRef.value?.group,
      radarDeviceMarkerRef.value?.scene,
      radarDeviceMarkerRef.value?.edges,
      radarScanBeamRef.value?.beamsGroup,
      thickAxesRef.value?.group,
      trackingRegionRef.value?.group,
    ],
    () => {
      addChildComponentsToScene()
    },
    { deep: true }
  )

  // 渲染循环
  animate()

  // 监听窗口大小变化
  window.addEventListener('resize', onWindowResize)
}

// 渲染循环
const animate = () => {
  requestAnimationFrame(animate)

  if (controls.value) {
    controls.value.update()
  }

  if (renderer.value && scene.value && camera.value) {
    renderer.value.render(scene.value, camera.value)
  }
}

// 窗口大小变化处理
const onWindowResize = () => {
  if (!canvasContainer.value || !camera.value || !renderer.value) return

  camera.value.aspect = canvasContainer.value.clientWidth / canvasContainer.value.clientHeight
  camera.value.updateProjectionMatrix()
  renderer.value.setSize(canvasContainer.value.clientWidth, canvasContainer.value.clientHeight)
}

// 监听设备参数变化
watch(
  () => props.devParam,
  (newDevParam) => {
    if (!newDevParam) return

    const timer = setTimeout(() => {
      resetSignal.value++
      topSignal.value = 0
      toggleAutoSignal.value = 0
      autoActive.value = false
      clearRenderData()
      clearStaleTimer()
    }, 50)

    return () => clearTimeout(timer)
  },
  { deep: true }
)

// 监听重置信号
watch(resetSignal, () => {
  resetCamera()
})

// 监听正视信号
watch(frontSignal, () => {
  setFrontView()
})

// 监听俯视信号
watch(topSignal, () => {
  setTopView()
})

// 监听自动环绕信号
watch(toggleAutoSignal, () => {
  autoActive.value = !autoActive.value
  if (autoActive.value) {
    startAutoRotate()
  } else {
    stopAutoRotate()
  }
})

// 监听数据变化和超时
watch(
  [() => props.radarTargets, () => props.pointClouds],
  ([newTargets, newPointClouds]) => {
    // 更新渲染数据 - 无论是否有devParam都要更新
    renderTargets.value = newTargets || []
    renderPointClouds.value = newPointClouds || []

    // 只有当devParam存在时才启动看门狗定时器
    if (props.devParam && props.staleTimeoutMs >= 0) {
      clearStaleTimer()
      staleTimerRef.value = setTimeout(() => {
        if (props.isReplay) return
        clearRenderData()
      }, props.staleTimeoutMs)
    }
  },
  { deep: true }
)

// 生命周期
onMounted(() => {
  initScene()
})

onUnmounted(() => {
  // 停止自动环绕
  stopAutoRotate()

  // 停止平滑过渡动画
  if (cameraTransitionId) {
    cancelAnimationFrame(cameraTransitionId)
    cameraTransitionId = null
  }

  // 清理资源
  window.removeEventListener('resize', onWindowResize)
  clearStaleTimer()

  if (renderer.value) {
    renderer.value.dispose()
    if (canvasContainer.value && renderer.value.domElement) {
      canvasContainer.value.removeChild(renderer.value.domElement)
    }
  }
})
</script>

<style scoped lang="less">
.radar-player-3d {
  width: 100%;
  height: 100%;
  position: relative;
}

.canvas-container {
  width: 100%;
  height: 100%;
  background: #000;
}
</style>
